import { useMemo } from "react";
import { parseMoodTimestamp } from "utils/common/timestampUtils";
import {
  getMondayOfWeek,
  isInWeek,
  getWeekDays,
  formatWeekLabel,
  getLastEndedWeek,
} from "utils/common/weekUtils";
import type { Mood } from "../../../types/models";

export interface WeekInfo {
  key: string;
  label: string;
  weekdays: Date[];
}

export interface UseWeekManagementReturn {
  availableWeeks: WeekInfo[];
  selectedWeekData: WeekInfo | null;
  effectiveSelectedWeek: string;
  lastEndedWeekPeriod: string;
  lastEndedWeek: Date[];
}

export function useWeekManagement(
  moods: Mood[],
  selectedWeek: string
): UseWeekManagementReturn {
  const lastEndedWeek = useMemo(() => getLastEndedWeek(), []);

  const availableWeeks = useMemo(() => {
    if (!moods || moods.length === 0) {
      return [];
    }

    const weekBuckets = new Map<string, { monday: Date; moods: Mood[] }>();

    moods.forEach((mood: Mood) => {
      const moodDate = parseMoodTimestamp(mood);
      if (!moodDate) return;

      const normalizedDate = new Date(
        Date.UTC(
          moodDate.getUTCFullYear(),
          moodDate.getUTCMonth(),
          moodDate.getUTCDate()
        )
      );

      const monday = getMondayOfWeek(normalizedDate);
      const key = monday.toISOString().slice(0, 10);

      if (isInWeek(normalizedDate, monday)) {
        if (!weekBuckets.has(key)) {
          weekBuckets.set(key, { monday, moods: [] });
        }
        weekBuckets.get(key)!.moods.push(mood);
      }
    });

    const weeks = Array.from(weekBuckets.entries())
      .map(([key, { monday }]) => {
        const weekdays = getWeekDays(monday);
        return {
          key,
          label: formatWeekLabel(weekdays),
          weekdays,
        };
      })
      .sort((a, b) => b.key.localeCompare(a.key));

    return weeks;
  }, [moods]);

  const defaultWeekKey = availableWeeks[0]?.key || "";
  const effectiveSelectedWeek = selectedWeek || defaultWeekKey;
  const selectedWeekData =
    availableWeeks.find((w) => w.key === effectiveSelectedWeek) ||
    availableWeeks[0] ||
    null;

  const lastEndedWeekPeriod = useMemo(() => {
    const weekToShow =
      selectedWeekData ||
      (lastEndedWeek.length >= 5
        ? {
            weekdays: lastEndedWeek,
          }
        : null);

    if (!weekToShow || weekToShow.weekdays.length < 5) return "";

    return formatWeekLabel(weekToShow.weekdays);
  }, [lastEndedWeek, selectedWeekData]);

  return {
    availableWeeks,
    selectedWeekData,
    effectiveSelectedWeek,
    lastEndedWeekPeriod,
    lastEndedWeek,
  };
}
